/* eslint-disable spaced-comment */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { FormGroup, ModalFooter, Label } from "reactstrap";
import useFetcher from "../../../hooks/useFetcher";
import { fetchNovedadPiAsignable } from "../../../services/novedadesServices";
import { StyledSelect } from "../../../components/StyledSelect";
import { handleErrors } from "../../../services/handleErrors";
import SpinnerButton from "../../../components/Button";
import { updateAreaCentralizado, postSubclientArea } from "../../../services/areaServices";
import { fetchDeliverDateCategorias } from "../../../services/subclientesServices";
import ConfirmModal from "./ConfirmModal";
import { fetchPurchaseCategorias } from "../../../services/enumFacturacionServices";
import compareDataChanges from "../../../utils/compareDataChanges";
import ConfirmCentralizado from "./ConfirmCentralizado";
import { useAuth } from "../../../context/AuthContext";
import modulos from "../../../mocks/modulos";
import { areaUtil } from "../../../utils/areaUtil";

const Facturacion = ({ centralizado, ClientId, subClient, subclientAreaData, handleChangesToSave }) => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser && currentUser.haveAdminAccess(modulos.CLIENTES);
  const isAudit = currentUser && currentUser.haveAuditAccess(modulos.CLIENTES);
  const isOwner = useMemo(() => areaUtil(subclientAreaData).isOwner(currentUser.user), [subclientAreaData, currentUser.user]);
  const readonly = (!isAdmin && !isOwner) || isAudit;

  const [isLoading, setIsLoading] = useState(false);
  const [confirmarModal, setConfirmarModal] = useState(false);
  const [centralizadoModal, setCentralizadoModal] = useState(false);
  const [changes, setChanges] = useState({
    responsableId: false,
    liderId: false,
    fechaDeEntrega: false,
    fechaEspecial: false,
    observaciones: false,
    envio: false,
    forma_de_envio: false,
    orden_de_compra: false,
  });
  const formik = useFormik({
    initialValues: {
      responsableId: (subclientAreaData && subclientAreaData.responsableId) || null,
      liderId: (subclientAreaData && subclientAreaData.liderId) || null,
      envio: (subclientAreaData && subclientAreaData.envio) || null,
      forma_de_envio: (subclientAreaData && subclientAreaData.forma_de_envio) || null,
      orden_de_compra: (subclientAreaData && subclientAreaData.orden_de_compra) || null,
      fechaDeEntrega: (subclientAreaData && subclientAreaData.fechaDeEntrega) || null,
      fechaEspecial: (subclientAreaData && subclientAreaData.fechaEspecial) || null,
      contacto: (subClient && subClient.contacto) || "",
      observaciones: (subclientAreaData && subclientAreaData.observaciones) || "",
    },
    onSubmit: async (area) => {
      setIsLoading(true);
      const filteredInfo = compareDataChanges(area, subclientAreaData);
      try {
        if (subclientAreaData) {
          await updateAreaCentralizado(subclientAreaData.id, ClientId, filteredInfo);
        } else {
          await postSubclientArea(filteredInfo);
        }
        setConfirmarModal(false);
        setCentralizadoModal(false);
        handleChangesToSave(false);
        setChanges({
          responsableId: false,
          liderId: false,
          fechaDeEntrega: false,
          fechaEspecial: false,
          observaciones: false,
          envio: false,
          forma_de_envio: false,
          orden_de_compra: false,
        });
        toast.success("Área actualizada");
        setIsLoading(false);
      } catch (error) {
        handleErrors(error, toast.error);
        setIsLoading(false);
      }
    },
    validationSchema: Yup.object().shape({
      responsableId: Yup.number().nullable(),
      liderId: Yup.number().nullable(),
      observaciones: Yup.string().nullable(),
      fechaDeEntrega: Yup.number().nullable(),
      fechaEspecial: Yup.number().nullable(),
      forma_de_envio: Yup.number().nullable(),
      orden_de_compra: Yup.number().nullable(),
      envio: Yup.number().nullable(),
    }),
  });

  const [pisAsignable, isLoadingAsignable] = useFetcher(fetchNovedadPiAsignable);
  const [compraOptions, isLoadingCompraOptions] = useFetcher(fetchPurchaseCategorias);
  const [fechaEntregaOptions, isLoadingFechaEntregaOptions] = useFetcher(fetchDeliverDateCategorias);

  const piOptions = pisAsignable
    ? pisAsignable.map((assigned) => {
        return { label: assigned.nombreApellido, value: assigned.itemId };
      })
    : [];

  const responsableValue = piOptions.find((option) => {
    return option.value === formik.values.responsableId;
  });

  const liderValue = piOptions.find((option) => {
    return option.value === formik.values.liderId;
  });

  const purchaseOrderOptions = compraOptions.purchaseOrder
    ? compraOptions.purchaseOrder.map((option) => {
        return { label: option.text, value: option.id };
      })
    : [];

  const shippingMethodOptions = compraOptions.shippingMethod
    ? compraOptions.shippingMethod.map((option) => {
        return { label: option.text, value: option.id };
      })
    : [];

  const shippingOptions = compraOptions.shipping
    ? compraOptions.shipping.map((option) => {
        return { label: option.text, value: option.id };
      })
    : [];

  const deliverDateOptions =
    fechaEntregaOptions.entregaFC && fechaEntregaOptions.entregaFC.type
      ? fechaEntregaOptions.entregaFC.type.map((option) => {
          return { label: option.text, value: option.id };
        })
      : [];
  const fechaEspecialOptions = Array.from({ length: 30 }, (_, i) => ({
    value: i + 1,
    label: (i + 1).toString(),
  }));
  const ordenCompraValue = purchaseOrderOptions.find((option) => {
    return option.value === formik.values.orden_de_compra;
  });
  const formaEnvioValue = shippingMethodOptions.find((option) => {
    return option.value === formik.values.forma_de_envio;
  });
  const envioValue = shippingOptions.find((option) => {
    return option.value === formik.values.envio;
  });

  const fechaDeEntrega = deliverDateOptions.find((option) => {
    return option.value === formik.values.fechaDeEntrega;
  });
  const fechaEspecialValue = fechaEspecialOptions.find((option) => {
    return option.value === formik.values.fechaEspecial;
  });

  const handleBlur = (value) => {
    if (subclientAreaData) {
      if (formik.values[value] == null) {
        handleChangesToSave(true);
        setChanges((prevState) => ({ ...prevState, [value]: true }));
        return;
      }
      if (formik.values[value] !== subclientAreaData[value]) {
        handleChangesToSave(true);
        setChanges((prevState) => ({ ...prevState, [value]: true }));
        return;
      }
      if (formik.values[value] === subclientAreaData[value]) {
        handleChangesToSave(false);
        setChanges((prevState) => ({ ...prevState, [value]: false }));
        return;
      }
    }
    if (formik.values[value] != null || formik.values[value] !== "") {
      handleChangesToSave(true);
      setChanges((prevState) => ({ ...prevState, [value]: true }));
    } else {
      handleChangesToSave(false);
      setChanges((prevState) => ({ ...prevState, [value]: false }));
    }
  };

  return (
    <>
      {confirmarModal && <ConfirmModal isLoading={isLoading} toggle={() => setConfirmarModal(!confirmarModal)} handleSubmit={formik.handleSubmit} />}
      {centralizadoModal && (
        <ConfirmCentralizado
          ClientId={ClientId}
          area="facturacion"
          areaColor="#ad1aac"
          isLoading={isLoading}
          toggle={() => setCentralizadoModal(!centralizadoModal)}
          handleSubmit={formik.handleSubmit}
        />
      )}
      <FormGroup className="d-flex justify-content-between">
        <Label htmlFor="responsable" className="col-form-label p-0 w-100">
          Responsable
          <StyledSelect
            id="responsableId"
            name="responsableId"
            className="w-100"
            formik={formik}
            required
            value={responsableValue}
            placeholder="Seleccione responsable"
            isClearable
            disabled={formik.isSubmitting || isLoadingAsignable}
            isLoading={isLoadingAsignable}
            options={piOptions}
            onBlur={() => handleBlur("responsableId")}
            isDisabled={readonly}
          />
          {changes.responsableId ? <div className="input-feedback">Cambios por guardar</div> : null}
        </Label>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between">
        <Label htmlFor="lider" className="col-form-label p-0 w-100">
          Líder
          <StyledSelect
            id="liderId"
            name="liderId"
            className="col-form-label p-0 w-100"
            formik={formik}
            required
            value={liderValue}
            placeholder="Seleccione lider"
            isClearable
            isLoading={isLoadingAsignable}
            disabled={formik.isSubmitting || isLoadingAsignable}
            options={piOptions}
            onBlur={() => handleBlur("liderId")}
            isDisabled={readonly}
          />
          {changes.liderId ? <div className="input-feedback">Cambios por guardar</div> : null}
        </Label>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between">
        <Label htmlFor="orden_de_compra" className="col-form-label p-0 w-100">
          Orden de compra
          <StyledSelect
            id="orden_de_compra"
            name="orden_de_compra"
            className="col-form-label p-0 w-100"
            formik={formik}
            required
            value={ordenCompraValue}
            placeholder="Seleccione orden"
            isClearable
            isLoading={isLoadingAsignable}
            disabled={formik.isSubmitting || isLoadingCompraOptions}
            options={purchaseOrderOptions}
            onBlur={() => handleBlur("orden_de_compra")}
            isDisabled={readonly}
          />
          {changes.orden_de_compra ? <div className="input-feedback">Cambios por guardar</div> : null}
        </Label>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between">
        <Label htmlFor="forma_de_envio" className="col-form-label p-0 w-100">
          Forma de envío
          <StyledSelect
            id="forma_de_envio"
            name="forma_de_envio"
            className="col-form-label p-0 w-100"
            formik={formik}
            required
            value={formaEnvioValue}
            placeholder="Seleccione forma de envío"
            isClearable
            isLoading={isLoadingAsignable}
            disabled={formik.isSubmitting || isLoadingCompraOptions}
            options={shippingMethodOptions}
            onBlur={() => handleBlur("forma_de_envio")}
            isDisabled={readonly}
          />
          {changes.forma_de_envio ? <div className="input-feedback">Cambios por guardar</div> : null}
        </Label>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between">
        <Label htmlFor="envio" className="col-form-label p-0 w-100">
          Envío
          <StyledSelect
            id="envio"
            name="envio"
            className="col-form-label p-0 w-100"
            formik={formik}
            required
            value={envioValue}
            placeholder="Seleccione envío"
            isClearable
            isLoading={isLoadingAsignable}
            disabled={formik.isSubmitting || isLoadingCompraOptions}
            options={shippingOptions}
            onBlur={() => handleBlur("envio")}
            isDisabled={readonly}
          />
          {changes.envio ? <div className="input-feedback">Cambios por guardar</div> : null}
        </Label>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between">
        <div className="col-form-group row p-0 w-100 align-items-start" style={{ margin: 0 }}>
          <Label htmlFor="fechaDeEntrega" className={formik.values.fechaDeEntrega === 100 ? "col-form-label p-0 w-50" : "col-form-label p-0 w-100"}>
            Fecha de Entrega
            <StyledSelect
              id="fechaDeEntrega"
              name="fechaDeEntrega"
              className="col-form-label p-0 w-100"
              formik={formik}
              required
              value={fechaDeEntrega}
              placeholder="Tipo de entrega"
              isClearable
              isLoading={isLoadingFechaEntregaOptions}
              disabled={formik.isSubmitting || isLoadingFechaEntregaOptions}
              options={deliverDateOptions}
              onBlur={() => handleBlur("fechaDeEntrega")}
              isDisabled={readonly}
            />
            {changes.fechaDeEntrega ? <div className="input-feedback">Cambios por guardar</div> : null}
          </Label>
          {formik.values.fechaDeEntrega === 100 && (
            <Label htmlFor="fechaEspecial" className="col-form-label p-0 w-50">
              Fecha Especial
              <StyledSelect
                id="fechaEspecial"
                name="fechaEspecial"
                className="col-form-label p-0 w-100"
                formik={formik}
                required
                value={fechaEspecialValue}
                placeholder="Seleccionar fecha"
                options={fechaEspecialOptions}
                isClearable
                disabled={formik.isSubmitting}
                onBlur={() => handleBlur("fechaEspecial")}
                isDisabled={readonly}
              />
              {changes.fechaEspecial ? <div className="input-feedback">Cambios por guardar</div> : null}
            </Label>
          )}
        </div>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between" style={{ margin: 0 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <Label htmlFor="contacto" className="col-form-label p-0 w-100">
            Contacto
          </Label>
          <textarea name="contacto" cols="63" rows="3" value={formik.values.contacto} onChange={formik.handleChange} onBlur={() => handleBlur("contacto")} disabled />
          {changes.contacto ? <div className="input-feedback">Cambios por guardar</div> : null}
        </div>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between" style={{ margin: 0 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <Label htmlFor="observaciones" className="col-form-label p-0 w-100">
            Observaciones
          </Label>
          <textarea
            name="observaciones"
            cols="63"
            rows="3"
            value={formik.values.observaciones}
            onChange={formik.handleChange}
            onBlur={() => handleBlur("observaciones")}
            disabled={readonly}
          />
          {changes.observaciones ? <div className="input-feedback">Cambios por guardar</div> : null}
        </div>
      </FormGroup>
      <ModalFooter>
        <FormGroup className="mb-0 px-3">
          {!readonly && (
            <SpinnerButton
              className="btn btn-primary btn-block"
              isLoading={false}
              disabled={false}
              onClick={() => (centralizado ? setCentralizadoModal(!centralizadoModal) : setConfirmarModal(!confirmarModal))}
            >
              Guardar Cambios
            </SpinnerButton>
          )}
        </FormGroup>
      </ModalFooter>
    </>
  );
};

export default Facturacion;
